import {
  CheckCircleOutline,
  Refresh,
} from "@material-ui/icons";
import Button from "components/Button";
import { observer } from "mobx-react-lite";
import { useProject } from "pages/Workspace/hooks";
import React, { useCallback, useEffect, useState } from "react";
import { useStore } from "store";
import styled from "styled-components";
import { Toast } from "utils";

type Props = {
  onPostUpdate?: () => Promise<void>;
  disabled?: boolean;
  // Keep the button disabled until the project's formulation sign off is completed.
  requireSignOff?: boolean;
};

const SavedText = styled.p`
  text-transform: uppercase;
  margin-bottom: 0;
  span {
    font-weight: 600;
    font-size: 0.75rem;
    color: #74b469;
  }

  svg {
    margin-right: 0.625rem;
    fill: #74b469;
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const GenerateCostSheetButton: React.FC<Props> = ({
  onPostUpdate,
  disabled,
  requireSignOff,
}) => {
  const store = useStore();
  const project = useProject();

  const [costSheetGenerated, setCostSheetGenerated] = useState(false);

  const signOff = project
    ? store.formulationApprovals.ofProject(project.id)
    : undefined;
  const awaitingSignOff = !!requireSignOff && signOff?.status !== "completed";
  const isDisabled = disabled || awaitingSignOff;

  useEffect(() => {
    if (!requireSignOff || !project || signOff) return;
    // Non-admins get 403 here; the button just stays disabled for them.
    store.formulationApprovals
      .list({ project: project.id })
      .catch(() => undefined);
  }, [project, requireSignOff, signOff, store.formulationApprovals]);

  const handleGenerateCostSheetClick = useCallback(async () => {
    if (!project) {
      return;
    }

    project.setLoading();

    try {
      await Promise.all([
        store.requirements.saveAll(project.id),
        store.formulations.saveAll(project.id),
      ]);
      await project.generateCostSheet();
      setCostSheetGenerated(true);
      await store.projects.detail(project.id);
    } catch (e) {
      Toast.danger("Cost sheet (frame) could not be generated.");
      return;
    } finally {
      project.setNotLoading();
    }

    await onPostUpdate?.();

    Toast.success("Cost sheet (frame) updated.");
  }, [
    onPostUpdate,
    project,
    store.formulations,
    store.projects,
    store.requirements,
  ]);

  useEffect(() => {
    if (project?.isCostSheetDirty) {
      setCostSheetGenerated(false);
    }
  }, [project?.isCostSheetDirty]);

  return (
    <>
      <Button
        variant={project?.isCostSheetDirty ? "cost-sheet-pending" : "primary"}
        size="thin"
        onClick={handleGenerateCostSheetClick}
        disabled={project?.loading || isDisabled}
        title={isDisabled ? "Get sign off approval before updating the cost sheet." : undefined}
      >
        <Refresh /> Update Cost Sheet (Frame)
      </Button>
      {costSheetGenerated && (
        <SavedText>
          <CheckCircleOutline />
          <span>All changes have been saved.</span>
        </SavedText>
      )}
    </>
  );
};

export default observer(GenerateCostSheetButton);
